import { Box, Center, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ACCEPT_FILES_TYPES } from '@dealstack/frontend/shared/components/MultiFileDropZone/MultiFileDropZone';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB } from '../../../ingestion.constants';
import type { IngestionUploadsState } from '../../useIngestionUploads.types';
import { uploadedCount, uploadsInFlight } from '../../useIngestionUploads.utils';
import { UploadedFileCard } from './UploadedFileCard';
import { UploadPartNotices } from './UploadPartNotices';
import { DROPZONE_STYLES, UPLOAD_FADE_IN_STYLES } from './UploadPanel.styles';
import {
	DS_AI_COLORS,
	DS_AI_MOTION,
	dsAiMoveMs,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';

interface Props {
	uploads: IngestionUploadsState;
}

/** The upload panel, empty or with the user's pending uploads on the deal. */
export const UploadPanel = ({ uploads }: Props) => {
	const tCapManage = useTranslationCapManage();

	return (
		<Stack gap="sm" p="sm" styles={{ root: UPLOAD_FADE_IN_STYLES }}>
			<Dropzone
				styles={DROPZONE_STYLES}
				data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_DROPZONE}
				accept={ACCEPT_FILES_TYPES.pdf}
				maxSize={MAX_UPLOAD_BYTES}
				multiple
				onDrop={uploads.onDrop}
				onReject={uploads.onReject}
			>
				<Stack gap="sm" align="stretch" justify="flex-start">
					<Center
						component="span"
						inline
						data-upload-icon
						w="2.5rem"
						h="2.5rem"
						styles={{
							root: {
								// Not `bg`/`c`: a style prop is inline, and the dropzone's
								// `[data-accept]` rule has to be able to recolour the tile.
								background: defaultTheme.v3.colors.gray[0],
								color: defaultTheme.v3.colors.dark[0],
								borderRadius: '50%',
								transition: `background-color ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing}, color ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing}, transform ${dsAiMoveMs(DS_AI_MOTION.baseMs)} ${DS_AI_MOTION.easing}`,
							},
						}}
					>
						<Icon variant="upload-v3-thin" size="lg" />
					</Center>
					<Stack gap={4} fz="md" c="dark.0">
						<Dropzone.Idle>
							<span>
								<Text span fw={600} c="blue.6">
									{tCapManage('dealstack_ai.ingest.upload.click_to_upload')}
								</Text>{' '}
								{tCapManage('dealstack_ai.ingest.upload.or_drag_and_drop')}
							</span>
						</Dropzone.Idle>
						<Dropzone.Accept>
							<Text span fw={600} c="blue.6">
								{tCapManage('dealstack_ai.ingest.upload.drop_to_upload')}
							</Text>
						</Dropzone.Accept>
						<Dropzone.Reject>
							<span>{tCapManage('dealstack_ai.ingest.upload.drop_rejected')}</span>
						</Dropzone.Reject>
						<span>
							{tCapManage('dealstack_ai.ingest.upload.constraints', {
								maxMb: MAX_UPLOAD_MB,
							})}
						</span>
					</Stack>
				</Stack>
			</Dropzone>
			<UploadPartNotices uploads={uploads} />
			{uploads.isError && (
				<Text component="p" size="md" m={0} c={DS_AI_COLORS.errorText} role="alert">
					{tCapManage('dealstack_ai.ingest.upload.list_failed')}
				</Text>
			)}
			{uploads.cards.length > 0 && (
				<>
					<Text component="h3" size="md" fw={600} c="dark.4" m={0}>
						{uploadsInFlight(uploads.cards)
							? tCapManage('dealstack_ai.ingest.upload.uploading_heading', {
									done: uploadedCount(uploads.cards),
									count: uploads.cards.length,
								})
							: tCapManage('dealstack_ai.ingest.upload.uploaded_heading', {
									count: uploads.cards.length,
								})}
					</Text>
					<Box
						component="ul"
						display="grid"
						m={0}
						p={0}
						style={{
							gridTemplateColumns: 'repeat(auto-fill, minmax(17rem, 1fr))',
							gap: '8px',
							listStyle: 'none',
						}}
					>
						{uploads.cards.map((card) => (
							<UploadedFileCard key={card.key} card={card} uploads={uploads} />
						))}
					</Box>
				</>
			)}
		</Stack>
	);
};
